import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Bell, LogOut, Stethoscope, Users, Wallet, Activity, Building2, ChevronRight,
  BarChart3, Settings as SettingsIcon,
} from "lucide-react";
import AddDoctorModal from "@/components/AddDoctorModal";
import AddPatientModal from "@/components/AddPatientModal";
import ChatPanel from "@/components/ChatPanel";
import OrgSettings from "@/components/OrgSettings";
import FollowupsPanel from "@/components/FollowupsPanel";
import FeedbackPanel from "@/components/FeedbackPanel";
import TrialBadge from "@/components/TrialBadge";
import SmsLogsDialog from "@/components/SmsLogsDialog";
import GovSchemeBadge from "@/components/GovSchemeBadge";
import GovVerificationActions from "@/components/GovVerificationActions";
import ReceptionistsManagement from "@/components/ReceptionistsManagement";
import SettingsTab from "@/components/SettingsTab";
import AdminIPDAnalytics from "@/pages/AdminIPDAnalytics";

/* -------------------------------------------------------------------------- */
/*  OverviewTab — stats + doctors table + reports + follow-ups + feedback     */
/* -------------------------------------------------------------------------- */

function OverviewTab({ user }) {
  const [stats, setStats] = useState({
    total_doctors: 0, today_patients: 0, today_earnings: 0,
    commission_per_patient: 0, platform_commission: 0, net_earnings: 0,
  });
  const [doctors, setDoctors] = useState([]);
  const [earnings, setEarnings] = useState({ rows: [], grand_total: 0 });
  const [patientsModal, setPatientsModal] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [patientsList, setPatientsList] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportFilters, setReportFilters] = useState({ date: "", q: "" });

  const load = async () => {
    const [s, d, e] = await Promise.all([
      api.get("/dashboard/admin"),
      api.get("/doctors"),
      api.get("/dashboard/earnings"),
    ]);
    setStats(s.data);
    setDoctors(d.data);
    setEarnings(e.data);
    if (!selectedDoctorId && d.data[0]) setSelectedDoctorId(d.data[0].id);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedDoctorId) return;
    api.get(`/dashboard/patients-by-doctor?doctor_id=${selectedDoctorId}`)
      .then((r) => setPatientsList(r.data));
  }, [selectedDoctorId]);

  return (
    <div className="space-y-8" data-testid="admin-tab-content-overview">
      {/* Trial banner */}
      {stats.trial?.active && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50/70">
          <TrialBadge trial={stats.trial} />
          <p className="text-xs text-emerald-800/80 max-w-xl">
            You're on the launch offer — pay only <strong>₹1 per settled patient</strong> instead of the standard commission until{" "}
            <strong>{new Date(stats.trial.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</strong>.
          </p>
        </div>
      )}

      {/* Stats */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="medical-card p-6" data-testid="stat-doctors">
          <div className="flex items-center justify-between">
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Total Doctors</p>
            <Users className="w-4 h-4 text-sky-500" />
          </div>
          <p className="font-heading text-4xl font-bold mt-3 text-slate-900">{stats.total_doctors}</p>
        </Card>

        <Dialog>
          <DialogTrigger asChild>
            <Card className="medical-card p-6 cursor-pointer hover:-translate-y-0.5" data-testid="stat-earnings">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Today Earning</p>
                <Wallet className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="font-heading text-4xl font-bold mt-3 text-emerald-600">₹{stats.today_earnings}</p>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">View breakdown <ChevronRight className="w-3 h-3" /></p>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Today Earnings Breakdown</DialogTitle></DialogHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Doctor</TableHead>
                  <TableHead className="text-right">Patients</TableHead>
                  <TableHead className="text-right">Fee/Patient</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {earnings.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500">No earnings yet today</TableCell></TableRow>
                ) : earnings.rows.map((r) => (
                  <TableRow key={r.doctor_id}>
                    <TableCell>{r.doctor_name}</TableCell>
                    <TableCell className="text-right">{r.patients_count}</TableCell>
                    <TableCell className="text-right">₹{r.fee_per_patient}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">₹{r.total_earning}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-semibold">Gross</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">₹{earnings.grand_total}</TableCell>
                </TableRow>
                {stats.commission_per_patient > 0 && (
                  <>
                    <TableRow className="bg-amber-50">
                      <TableCell colSpan={3} className="text-right">Platform commission ({stats.completed_count} × ₹{stats.commission_per_patient})</TableCell>
                      <TableCell className="text-right text-amber-700">− ₹{stats.platform_commission}</TableCell>
                    </TableRow>
                    <TableRow className="bg-emerald-50">
                      <TableCell colSpan={3} className="text-right font-bold">Net Profit</TableCell>
                      <TableCell className="text-right font-bold text-emerald-700" data-testid="net-profit-row">₹{stats.net_earnings}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>

        <Dialog open={patientsModal} onOpenChange={setPatientsModal}>
          <DialogTrigger asChild>
            <Card className="medical-card p-6 cursor-pointer hover:-translate-y-0.5" data-testid="stat-patients">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Today Total Patient</p>
                <Stethoscope className="w-4 h-4 text-sky-500" />
              </div>
              <p className="font-heading text-4xl font-bold mt-3 text-sky-600">{stats.today_patients}</p>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">View list <ChevronRight className="w-3 h-3" /></p>
            </Card>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Today Patients</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
                <SelectTrigger data-testid="patients-doctor-select" aria-label="Select doctor for patient list"><SelectValue placeholder="Choose doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`patients-doctor-option-${d.id}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patientsList.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500">No patients</TableCell></TableRow>
                  ) : patientsList.map((p) => {
                    const isGov = ["rghs", "ayushman"].includes((p.payment_method || "").toLowerCase());
                    const needsAction = isGov && !p.gov_verified && !p.gov_verification_rejected && p.status === "pending_counter_verification";
                    return (
                      <TableRow key={p.id} data-testid={`patient-row-${p.id}`}>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span>{p.name}</span>
                            <GovSchemeBadge appointment={p} />
                          </div>
                        </TableCell>
                        <TableCell>{p.age}</TableCell>
                        <TableCell>{p.mobile}</TableCell>
                        <TableCell>
                          <Badge variant={p.status === "completed" ? "default" : p.status === "checking" ? "secondary" : p.status === "cancelled" ? "destructive" : "outline"}>
                            {p.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {needsAction ? (
                            <GovVerificationActions
                              appointment={p}
                              onDone={(updated) => setPatientsList((prev) => prev.map((row) => row.id === updated.id ? updated : row))}
                            />
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* Doctors table */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Doctors</h2>
            <p className="text-sm text-slate-500">Live diagnosis status and today&apos;s bookings</p>
          </div>
          <div className="flex gap-2">
            <AddPatientModal doctors={doctors} onCreated={load} />
            <AddDoctorModal onCreated={load} />
          </div>
        </div>

        <Card className="medical-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Doctor</TableHead>
                <TableHead>Specialization</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>CID</TableHead>
                <TableHead>Live Status</TableHead>
                <TableHead className="text-right">Today&apos;s Bookings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doctors.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12" data-testid="empty-doctors">No doctors yet — click &quot;Add Doctor&quot; to get started.</TableCell></TableRow>
              ) : doctors.map((d) => (
                <TableRow key={d.id} data-testid={`doctor-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                        {d.profile_pic ? <img src={fileUrl(d.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                          <span className="font-semibold text-slate-600">{d.name?.[0]}</span>}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{d.name}</div>
                        <div className="text-xs text-slate-500">{d.availability === "active" ? "Active" : "On Leave"} · {d.in_cabin ? "In Cabin" : "Out"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{d.specialization_en}</TableCell>
                  <TableCell className="text-slate-600">{d.mobile}</TableCell>
                  <TableCell className="font-mono text-slate-700">{d.cid}</TableCell>
                  <TableCell>
                    {d.current_ticket ? (
                      <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">#{d.current_ticket}</Badge>
                    ) : (
                      <Badge variant="outline">Idle</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{d.today_bookings || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </section>

      {/* Reports */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Reports</h2>
            <p className="text-sm text-slate-500">Search patient visits by date or name / mobile.</p>
          </div>
        </div>
        <Card className="medical-card p-5">
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <input type="date" value={reportFilters.date} onChange={(e) => setReportFilters({ ...reportFilters, date: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="report-date-input" />
            <input type="text" placeholder="Search by name or mobile" value={reportFilters.q} onChange={(e) => setReportFilters({ ...reportFilters, q: e.target.value })} className="h-10 px-3 rounded-md border border-slate-200 text-sm sm:col-span-2" data-testid="report-q-input" />
          </div>
          <Button onClick={async () => { const p = new URLSearchParams(); if (reportFilters.date) p.set("date", reportFilters.date); if (reportFilters.q) p.set("q", reportFilters.q); const { data } = await api.get(`/dashboard/admin/reports?${p}`); setReports(data); }} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="report-search-btn">Search</Button>
          <div className="mt-5 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No results — apply a filter above and search.</TableCell></TableRow>
                ) : reports.map((r) => (
                  <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                    <TableCell className="text-xs">{r.date}</TableCell>
                    <TableCell className="font-mono">#{r.ticket_number}</TableCell>
                    <TableCell>{r.name} <span className="text-xs text-slate-500">· {r.age}</span></TableCell>
                    <TableCell className="text-slate-600">{r.mobile}</TableCell>
                    <TableCell className="text-slate-600">{r.doctor_name}</TableCell>
                    <TableCell><Badge variant={r.status === "completed" ? "default" : "outline"}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right">₹{r.fee}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>

      {/* Follow-ups */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Follow-ups</h2>
            <p className="text-sm text-slate-500">Patients scheduled to return. Reminders fire automatically at 9 AM one day before.</p>
          </div>
        </div>
        <FollowupsPanel api={api} />
      </section>

      {/* Feedback */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-slate-900">Feedback</h2>
            <p className="text-sm text-slate-500">Post-visit patient ratings — sent automatically 2 hours after checkout.</p>
          </div>
        </div>
        <FeedbackPanel api={api} />
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ReceptionistsTab — dedicated tab wrapping the mgmt table + perms modal    */
/* -------------------------------------------------------------------------- */

function ReceptionistsTab() {
  return (
    <div className="space-y-6" data-testid="admin-tab-content-receptionists">
      <ReceptionistsManagement />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main dashboard                                                            */
/* -------------------------------------------------------------------------- */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  if (!user) return null;
  const isClinic = user.role === "clinic_admin";
  const orgLabel = isClinic ? "Clinic" : "Hospital";

  // Only hospital_admin sees IPD Analytics (per product spec).
  const tabs = [
    { id: "overview",       label: "Overview",       icon: BarChart3, visible: true },
    { id: "receptionists",  label: "Receptionists",  icon: Users,     visible: user.role === "hospital_admin" },
    { id: "ipd-analytics",  label: "IPD Analytics",  icon: Activity,  visible: user.role === "hospital_admin" },
    { id: "settings",       label: "Settings",       icon: SettingsIcon, visible: true },
  ].filter((t) => t.visible);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="px-6 py-3 flex items-center justify-between max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
              {isClinic ? <Activity className="w-5 h-5 text-white" /> : <Building2 className="w-5 h-5 text-white" />}
            </div>
            <div>
              <div className="font-heading text-base font-semibold text-slate-900" data-testid="org-name">{user.org_name}</div>
              <div className="text-xs text-slate-500">{orgLabel} Admin · {user.working_hours}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/analytics")} data-testid="analytics-nav-btn" className="gap-1.5">
              <BarChart3 className="w-4 h-4" /> Analytics
            </Button>
            <ChatPanel />
            <SmsLogsDialog />
            <Button variant="outline" size="icon" className="relative" data-testid="notification-bell">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-500" />
            </Button>
            <OrgSettings user={user} onSaved={() => { /* org save is self-contained; overview will re-fetch on next visit */ }} />
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Browser-style tab strip */}
        <div className="border-t border-slate-100">
          <div
            className="max-w-[1400px] mx-auto flex overflow-x-auto no-scrollbar"
            data-testid="admin-dashboard-tabs"
            role="tablist"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  role="tab"
                  aria-selected={active}
                  data-testid={`admin-tab-${tab.id}`}
                  className={[
                    "flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap",
                    "border-b-2 transition-colors duration-200",
                    active
                      ? "border-sky-500 text-sky-600"
                      : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 py-6 md:py-8 max-w-[1400px] mx-auto">
        {activeTab === "overview" && <OverviewTab user={user} />}
        {activeTab === "receptionists" && user.role === "hospital_admin" && <ReceptionistsTab />}
        {activeTab === "ipd-analytics" && user.role === "hospital_admin" && <AdminIPDAnalytics />}
        {activeTab === "settings" && <SettingsTab orgName={user.org_name} />}
      </main>
    </div>
  );
}
